<template>
  <slot :open-drawer="openDrawer" :open-drawer-at="openDrawerAt" />
  <ClientOnly>
    <TitledRichTextDrawer
      :content="selectedDrawerTab?.body"
      :cta="drawerCta"
      :full-height="true"
      :on-open-change="setDrawerOpen"
      :on-select-tab="selectDrawerTab"
      :open="isDrawerOpen"
      :selected-tab-index="effectiveDrawerTabIndex"
      :tabs="visibleDrawerTabs"
      :title="experienceCms.drawerTitle ?? ''"
    />
  </ClientOnly>
</template>

<script lang="ts" setup>
import { useStateKey, type UseState } from '@/injectionKeys';
import type { EnExperienceViewModel } from '@/models/enExperience';
import { extractVisibleDrawerTabs, hasRenderedDrawerTrigger, resolveDrawerTabIndex } from '@/models/enExperience';
import { sendGAWabunkaDrawerImpressionEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/drawerImpression';
import { sendGAWabunkaDrawerOpenEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/drawerOpen';
import { sendGAWabunkaDrawerTabViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/drawerTabView';
import TitledRichTextDrawer from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/pure/TitledRichTextDrawer/TitledRichTextDrawer.vue';

interface Props {
  experienceCms: EnExperienceViewModel;
  onClickCheckAvailability: () => void;
}

const props = defineProps<Props>();

const visibleDrawerTabs = computed(() => extractVisibleDrawerTabs(props.experienceCms.drawerTabs));
const isDrawerOpen = ref(false);
// タブの同一性は入稿順で持つ。tabKey は自由記述で重複・未入力があり得るため選択状態の識別子にしない
const selectedDrawerTabIndex = ref(0);
// 本文と選択状態がずれると「本文は出ているのに選択中のタブが無い」状態になるため、丸めた index を両方に使う
const effectiveDrawerTabIndex = computed(() => (selectedDrawerTabIndex.value < visibleDrawerTabs.value.length ? selectedDrawerTabIndex.value : 0));
const selectedDrawerTab = computed(() => visibleDrawerTabs.value[effectiveDrawerTabIndex.value]);
const setDrawerOpen = (open: boolean) => {
  isDrawerOpen.value = open;
};

// 読み終えたゲストをそのまま予約導線へ返す（一次指標が「起動 → 問い合わせ率」のため）。
// 予約ドロワーとはオーバーレイと本体の z-index が同じ体系なので、閉じてから渡さないと2枚重なる
const drawerCta = computed(() => ({
  label: 'Check availability',
  onClick: () => {
    setDrawerOpen(false);
    props.onClickCheckAvailability();
  },
}));
const experienceId = computed(() => props.experienceCms.id);
// tabKey は自由記述で前後の空白が混ざり得るため、GA へは突き合わせと同じ形で送る（D-003）
const drawerTabKeyAt = (index: number) => visibleDrawerTabs.value[index]?.tabKey?.trim() ?? '';

const selectDrawerTab = (index: number) => {
  if (index === effectiveDrawerTabIndex.value) return;
  selectedDrawerTabIndex.value = index;
  sendGAWabunkaDrawerTabViewEvent({ experienceId: experienceId.value, tabKey: drawerTabKeyAt(index) });
};
// tabKey は重複・未入力があり得るため、タブを並べて見せる起動元には位置で開く経路を渡す
const openDrawerAt = (index: number, label: string) => {
  if (index < 0 || index >= visibleDrawerTabs.value.length) return;
  selectedDrawerTabIndex.value = index;
  isDrawerOpen.value = true;
  const resolvedTabKey = drawerTabKeyAt(index);
  sendGAWabunkaDrawerOpenEvent({ experienceId: experienceId.value, tabKey: resolvedTabKey, triggerLabel: label });
  sendGAWabunkaDrawerTabViewEvent({ experienceId: experienceId.value, tabKey: resolvedTabKey });
};
const openDrawer = (tabKey: string, label: string) => {
  openDrawerAt(resolveDrawerTabIndex(props.experienceCms.drawerTabs, tabKey), label);
};

const useState = inject(useStateKey) as UseState;
// 回遊して戻ってきた体験で二重に送らないよう、送信済みの体験を積んで持つ。分母は1セッション1体験（D-008）
const drawerImpressionSentExperienceIds = useState<string[]>('wabunkaDrawerImpressionSentExperienceIds', () => []);
onMounted(() => {
  if (!hasRenderedDrawerTrigger(props.experienceCms)) return;
  if (drawerImpressionSentExperienceIds.value.includes(experienceId.value)) return;
  drawerImpressionSentExperienceIds.value.push(experienceId.value);
  sendGAWabunkaDrawerImpressionEvent({ experienceId: experienceId.value });
});
</script>
